/**
 * 全局唯一的 EIP-1193 Provider 解析入口。
 *
 * 解析优先级：
 *   1. 已缓存 provider（避免重复授权弹窗）
 *   2. 活跃的 WalletConnect 会话
 *   3. 同步可得的注入式 provider（window.ethereum / EIP-6963 已 announce）
 *   4. 短暂等待晚到的 EIP-6963 announce
 *   5. WalletConnect 弹窗（仅 caller 显式允许时）
 *
 * 返回的 provider 一律是 EIP-1193 兼容对象，调用方使用 provider.request / provider.on
 * 与原生 window.ethereum 用法完全一致。
 */

import {
  getBrowserEthereumProvider,
  waitForBrowserEthereumProvider
} from '@/utils/evmInjectedProvider'
import {
  connectViaWalletConnect,
  getActiveWalletConnectProvider,
  disconnectWalletConnect
} from '@/utils/walletConnectProvider'

let cachedProvider = null
let cachedSource = '' // 'injected' | 'wc'

function setCache(p, source) {
  cachedProvider = p
  cachedSource = source
  return p
}

/**
 * 同步取已解析的 provider，绝不触发任何 UI（不弹 WC）。
 */
export function getResolvedProviderSync() {
  if (cachedProvider) return cachedProvider
  const wc = getActiveWalletConnectProvider()
  if (wc) return setCache(wc, 'wc')
  const inj = getBrowserEthereumProvider()
  if (inj) return setCache(inj, 'injected')
  return null
}

export function getResolvedProviderSource() {
  return cachedSource
}

/**
 * 异步解析 provider。
 *
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=3000] - 等待 EIP-6963 晚到 announce 的时长
 * @param {boolean} [opts.allowWalletConnectFallback=false] - 注入路径无果时是否弹 WC 弹窗
 * @param {boolean} [opts.preferWalletConnect=false] - 跳过注入检测直接弹 WC
 * @returns {Promise<object|null>} EIP-1193 provider 或 null
 */
export async function resolveProvider(opts = {}) {
  const {
    timeoutMs = 3000,
    allowWalletConnectFallback = false,
    preferWalletConnect = false
  } = opts

  if (cachedProvider) return cachedProvider

  if (preferWalletConnect) {
    return await connectAndCacheWalletConnect()
  }

  const wc = getActiveWalletConnectProvider()
  if (wc) return setCache(wc, 'wc')

  const sync = getBrowserEthereumProvider()
  if (sync) return setCache(sync, 'injected')

  const waited = await waitForBrowserEthereumProvider({ timeoutMs })
  if (waited) return setCache(waited, 'injected')

  if (allowWalletConnectFallback) {
    console.log('[RESOLVE] no injected provider; falling back to WalletConnect')
    return await connectAndCacheWalletConnect()
  }

  console.warn('[RESOLVE] no provider available and WC fallback not requested')
  return null
}

/** 强制走 WalletConnect。用户取消返回 null。 */
export async function forceConnectWalletConnect() {
  return await connectAndCacheWalletConnect()
}

async function connectAndCacheWalletConnect() {
  try {
    const wc = await connectViaWalletConnect()
    if (wc) return setCache(wc, 'wc')
    return null
  } catch (e) {
    console.warn('[RESOLVE] WalletConnect connect failed/cancelled:', e && e.message ? e.message : e)
    return null
  }
}

/**
 * 重置缓存。可选同时断开 WC 会话。
 */
export async function clearResolvedProvider({ disconnectWc = false } = {}) {
  if (disconnectWc) {
    await disconnectWalletConnect()
  }
  cachedProvider = null
  cachedSource = ''
}
